import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useParams } from 'react-router-dom';
import uniqBy from 'lodash/uniqBy';
import { useTranslation } from 'react-i18next';
import { showToaster } from 'nallian-shared-ui/lib/lib/showToaster';
import { Flex, Loader } from 'nallian-shared-ui/lib/components';
import lookupService from 'nallian-shared-ui/lib/hoc/lookupService';
import BlobsForm from './BlobsForm';
import blobsService from '../../services/blobs/blobsService';
import createBlobService from '../../services/blobs/createBlobService';
import updateBlobService from '../../services/blobs/updateBlobService';
import apiRoutes from '../../../../app/api/routes';
import { HEADER_ID, FOOTER_ID } from './constants';

const EXTRACTED_KEYS = ['id', 'type', 'value'];

const mapFormToBlob = form => ({
  id: form?.id,
  type: form?.type,
  value: form?.en,
  languageOverrides: Object.keys(form)
    .map(k => {
      if (EXTRACTED_KEYS.find(ek => ek === k)) return false;
      return { languageId: k, value: form[k] };
    })
    .filter(l => l && l.value),
});

const Blobs = ({
  dispatchBlobsGet,
  blobsFetch,
  languagesFetch,
  dispatchCreateBlobPost,
  dispatchUpdateBlobPut,
  createBlobFetch,
  updateBlobFetch,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();

  useEffect(() => dispatchBlobsGet(id), [dispatchBlobsGet, id]);

  const initialLanguages = useMemo(() => {
    const languages = [
      ...(languagesFetch.value || []).map(l => ({
        id: l.id,
        active: Boolean(l.id === 'en'),
      })),
    ];

    (blobsFetch.value || []).forEach(b =>
      (b.languageOverrides || []).forEach(lo =>
        languages.push({ id: lo.languageId, active: false })
      )
    );

    return uniqBy(languages, 'id');
  }, [languagesFetch.value, blobsFetch.value]);

  const initialValues = useMemo(() => {
    const headerValues = (blobsFetch.value || []).find(b => b.type === HEADER_ID);
    const footerValues = (blobsFetch.value || []).find(b => b.type === FOOTER_ID);

    const header = { id: headerValues?.id, en: headerValues?.value, type: HEADER_ID };
    const footer = { id: footerValues?.id, en: footerValues?.value, type: FOOTER_ID };

    // eslint-disable-next-line no-unused-expressions
    headerValues?.languageOverrides?.forEach(l => {
      header[l.languageId] = l.value;
    });

    // eslint-disable-next-line no-unused-expressions
    footerValues?.languageOverrides?.forEach(l => {
      footer[l.languageId] = l.value;
    });

    return {
      header,
      footer,
    };
  }, [blobsFetch.value]);

  const handleSubmit = useCallback(
    ({ header, footer }) => {
      const callback = () => {
        dispatchBlobsGet(id);
        showToaster('success', t('blobsUpdated'));
      };
      if (header.id) dispatchUpdateBlobPut(id, header.id, undefined, mapFormToBlob(header));
      else dispatchCreateBlobPost(id, undefined, mapFormToBlob(header));

      if (footer.id) dispatchUpdateBlobPut(id, footer.id, callback, mapFormToBlob(footer));
      else dispatchCreateBlobPost(id, callback, mapFormToBlob(footer));
    },
    [dispatchBlobsGet, dispatchCreateBlobPost, dispatchUpdateBlobPut, id, t]
  );

  return (
    <Flex>
      {languagesFetch.pending || blobsFetch.pending ? (
        <Loader inline active />
      ) : (
        <BlobsForm
          loading={blobsFetch.pending}
          submitting={createBlobFetch.pending || updateBlobFetch.pending}
          languages={initialLanguages}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        />
      )}
    </Flex>
  );
};

Blobs.propTypes = {
  dispatchBlobsGet: PropTypes.func.isRequired,
  blobsFetch: PropTypes.object.isRequired,
  languagesFetch: PropTypes.func.isRequired,
  dispatchCreateBlobPost: PropTypes.func.isRequired,
  createBlobFetch: PropTypes.object.isRequired,
  dispatchUpdateBlobPut: PropTypes.func.isRequired,
  updateBlobFetch: PropTypes.object.isRequired,
};

export default compose(
  blobsService,
  createBlobService,
  updateBlobService,
  lookupService({ resource: 'languages', url: apiRoutes.getLanguages() })
)(Blobs);
